class StorageService {
  constructor() {
    this.keys = {
      USER: 'educapp_user',
      TOKEN: 'educapp_token',
      REFRESH_TOKEN: 'educapp_refresh_token',
      SETTINGS: 'educapp_settings',
      THEME: 'educapp_theme',
      SIDEBAR_STATE: 'educapp_sidebar_state'
    };
  }

  // Métodos para autenticación
  saveAuth(user, token, refreshToken = null) {
    try {
      this.setItem(this.keys.USER, user);
      this.setItem(this.keys.TOKEN, token);
      if (refreshToken) {
        this.setItem(this.keys.REFRESH_TOKEN, refreshToken);
      }
    } catch (error) {
      console.error('Error al guardar datos de autenticación:', error);
    }
  }

  getUser() {
    try {
      return this.getItem(this.keys.USER);
    } catch (error) {
      console.error('Error al obtener usuario:', error);
      return null;
    }
  }

  getToken() {
    try {
      return this.getItem(this.keys.TOKEN);
    } catch (error) {
      console.error('Error al obtener token:', error);
      return null;
    }
  }

  getRefreshToken() {
    try {
      return this.getItem(this.keys.REFRESH_TOKEN);
    } catch (error) {
      console.error('Error al obtener refresh token:', error);
      return null;
    }
  }

  updateUser(userData) {
    try {
      const currentUser = this.getUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, ...userData };
        this.setItem(this.keys.USER, updatedUser);
        return updatedUser;
      }
      return null;
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      return null;
    }
  }

  clearAuth() {
    try {
      this.removeItem(this.keys.USER);
      this.removeItem(this.keys.TOKEN);
      this.removeItem(this.keys.REFRESH_TOKEN);
    } catch (error) {
      console.error('Error al limpiar datos de autenticación:', error);
    }
  }

  // Métodos para configuraciones de la aplicación
  saveSettings(settings) {
    try {
      this.setItem(this.keys.SETTINGS, settings);
    } catch (error) {
      console.error('Error al guardar configuraciones:', error);
    }
  }

  getSettings() {
    try {
      return this.getItem(this.keys.SETTINGS) || {};
    } catch (error) {
      console.error('Error al obtener configuraciones:', error);
      return {};
    }
  }

  updateSettings(newSettings) {
    try {
      const currentSettings = this.getSettings();
      const updatedSettings = { ...currentSettings, ...newSettings };
      this.saveSettings(updatedSettings);
      return updatedSettings;
    } catch (error) {
      console.error('Error al actualizar configuraciones:', error);
      return {};
    }
  }

  // Métodos para tema
  saveTheme(theme) {
    try {
      this.setItem(this.keys.THEME, theme);
    } catch (error) {
      console.error('Error al guardar tema:', error);
    }
  }

  getTheme() {
    try {
      return this.getItem(this.keys.THEME) || 'light';
    } catch (error) {
      console.error('Error al obtener tema:', error);
      return 'light';
    }
  }

  // Métodos para estado del sidebar
  saveSidebarState(isOpen) {
    try {
      this.setItem(this.keys.SIDEBAR_STATE, isOpen);
    } catch (error) {
      console.error('Error al guardar estado del sidebar:', error);
    }
  }

  getSidebarState() {
    try {
      return this.getItem(this.keys.SIDEBAR_STATE) ?? true;
    } catch (error) {
      console.error('Error al obtener estado del sidebar:', error);
      return true;
    }
  }

  // Métodos genéricos de localStorage
  setItem(key, value) {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('LocalStorage quota exceeded. Clearing old data...');
        this.clearOldData();
        try {
          const serializedValue = JSON.stringify(value);
          localStorage.setItem(key, serializedValue);
        } catch (retryError) {
          console.error('Failed to save even after clearing old data:', retryError);
        }
      } else {
        console.error('Error saving to localStorage:', error);
      }
    }
  }

  getItem(key) {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return null;
      return JSON.parse(item);
    } catch (error) {
      console.error(`Error parsing localStorage item ${key}:`, error);
      // Si hay error al parsear, eliminar el item corrupto
      this.removeItem(key);
      return null;
    }
  }

  removeItem(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing localStorage item ${key}:`, error);
    }
  }

  // Limpiar datos antiguos para liberar espacio
  clearOldData() {
    try {
      // Lista de keys que podrían ser eliminadas para liberar espacio
      const nonEssentialKeys = ['temp_', 'cache_', 'draft_'];
      
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && nonEssentialKeys.some(prefix => key.startsWith(prefix))) {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('Error clearing old data:', error);
    }
  }

  // Obtener información sobre el uso de localStorage
  getStorageInfo() {
    try {
      let totalSize = 0;
      const itemSizes = {};

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);
        const size = (key.length + (value ? value.length : 0)) * 2; // 2 bytes por carácter
        itemSizes[key] = size;
        totalSize += size;
      }

      return {
        totalSize,
        itemCount: localStorage.length,
        items: itemSizes,
        formattedSize: this.formatBytes(totalSize)
      };
    } catch (error) {
      console.error('Error getting storage info:', error);
      return {
        totalSize: 0,
        itemCount: 0,
        items: {},
        formattedSize: '0 B'
      };
    }
  }

  // Formatear bytes en formato legible
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  // Limpiar todo el localStorage de la aplicación
  clearAll() {
    try {
      Object.values(this.keys).forEach(key => {
        this.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing all data:', error);
    }
  }

  // Verificar si localStorage está disponible
  isAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Método para exportar datos (para backup)
  exportData() {
    try {
      const data = {};
      Object.entries(this.keys).forEach(([name, key]) => {
        const value = this.getItem(key);
        if (value !== null) {
          data[name] = value;
        }
      });
      return data;
    } catch (error) {
      console.error('Error exporting data:', error);
      return {};
    }
  }

  // Método para importar datos (desde backup)
  importData(data) {
    try {
      Object.entries(data).forEach(([name, value]) => {
        const key = this.keys[name];
        if (key && value !== null) {
          this.setItem(key, value);
        }
      });
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  }
}

export const storageService = new StorageService();